import { CmsMeta } from '../cmsMeta';
import { ReactNode } from 'react';
import './InfoBox.css';
import { Link } from 'react-router-dom';
import { isExternalUrl } from '../../../utils/links';

type InfoBoxProps = {
	heading: string;
	/*
	 * Three props for one link, and all of them optional in practice: the render falls back
	 * from `cta` to the flat pair, and shows nothing when neither is given. Content uses the
	 * flat pair. Marked required, they made a correct example look like a broken one.
	 */
	cta?: { text: string; link: string };
	ctaLink?: string;
	ctaText?: string;
	image: string;
	size?: 'sm' | 'md' | 'lg';
	color?: string;
	children: ReactNode;
};

export const InfoBox = ({
	heading,
	cta,
	ctaLink,
	ctaText,
	image,
	size = 'sm',
	color = 'white',
	children,
}: InfoBoxProps): JSX.Element => {
	const link = cta?.link ?? ctaLink;
	const text = cta?.text ?? ctaText;

	return (
		<div className={`info-box info-box--${size}`}>
			<div className="content">
				{image && (
					<div className="content-right">
						<img className="cms-image" src={image} />{' '}
					</div>
				)}
				<div className={`content-left content-left--${color}`}>
					<h2 className="info-box-heading">{heading}</h2>
					<div>{children}</div>
					{link && text ? (
						<div className="cta">
							{/* the styling hangs off `.cta a`, so both spellings of the link look the same */}
							{isExternalUrl(link) ? <a href={link}>{text}</a> : <Link to={link}>{text}</Link>}
							<span className="cta-arrow">→</span>
						</div>
					) : null}
				</div>
			</div>
		</div>
	);
};

export const cmsMeta: CmsMeta = {
	description:
		'A picture, a heading, a short list and one link. Two or three of these side by side is the usual shape below the banners.',
	example: [
		'InfoBox',
		{
			index: 0,
			size: 'sm',
			color: 'green',
			image: 'https://cdn.ondio.com/assets/633eb352355343449e3103a4157317ad.jpg',
			heading: 'Günstig',
			ctaText: 'Alle Aktionen ansehen',
			ctaLink: '/aktionsangebote',
		},
		['ul', { index: 0 }, ['li', { index: 0 }, 'Niedrige Preise'], ['li', { index: 1 }, 'Riesiges Sortiment']],
	],
};
